import { API_BASE_URL, authFetch } from './api';


const BASE = `${API_BASE_URL}/scheduled-runs`;

export interface ScheduledRun {
  id: string;
  user_id: string;
  name: string;
  run_type: 'agentic_group' | 'content_upload';
  timing_mode: 'specific' | 'random';
  schedule_type: 'once' | 'daily';
  timezone: string;
  scheduled_at: string | null;
  random_window_start: string | null;
  random_window_end: string | null;
  max_runs_per_day: number;
  runs_completed_today: number;
  next_run_at: string | null;
  last_run_at: string | null;
  last_group_id: string | null;
  config: Record<string, any>;
  status: 'active' | 'paused' | 'completed' | 'cancelled' | 'running' | 'skipped';
  run_count: number;
  slack_bot_id: string | null;
  last_run_status: string | null;
  last_run_error: string | null;
  last_run_completed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface Execution {
  id: string;
  schedule_id: string | null;
  group_id: string | null;
  status: string;
  device_ids_attempted: string[];
  device_ids_skipped: { pad_id: string; reason: string }[];
  device_ids_succeeded: string[];
  error_message: string | null;
  steps_total: number | null;
  started_at: string;
  completed_at: string | null;
}

export interface CreateScheduleRequest {
  name: string;
  run_type: string;
  timing_mode: string;
  schedule_type: string;
  timezone?: string;
  scheduled_at?: string;
  random_window_start?: string;
  random_window_end?: string;
  max_runs_per_day?: number;
  config: Record<string, any>;
  slack_bot_id?: string;
}

export interface UpdateScheduleRequest {
  name?: string;
  timing_mode?: string;
  schedule_type?: string;
  timezone?: string;
  scheduled_at?: string;
  random_window_start?: string;
  random_window_end?: string;
  max_runs_per_day?: number;
  config?: Record<string, any>;
  status?: string;
  slack_bot_id?: string;
}

export async function listScheduledRuns(params?: {
  start_date?: string;
  end_date?: string;
  status?: string;
  run_type?: string;
}): Promise<ScheduledRun[]> {
  const searchParams = new URLSearchParams();
  if (params?.start_date) searchParams.set('start_date', params.start_date);
  if (params?.end_date) searchParams.set('end_date', params.end_date);
  if (params?.status) searchParams.set('status', params.status);
  if (params?.run_type) searchParams.set('run_type', params.run_type);

  const qs = searchParams.toString();
  const res = await authFetch(`${BASE}${qs ? `?${qs}` : ''}`);
  return res.json();
}

export async function createScheduledRun(data: CreateScheduleRequest): Promise<ScheduledRun> {
  const res = await authFetch(BASE, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return res.json();
}

export async function getScheduledRun(id: string): Promise<ScheduledRun> {
  const res = await authFetch(`${BASE}/${id}`);
  return res.json();
}

export async function updateScheduledRun(id: string, data: UpdateScheduleRequest): Promise<ScheduledRun> {
  const res = await authFetch(`${BASE}/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
  return res.json();
}

export async function deleteScheduledRun(id: string): Promise<void> {
  await authFetch(`${BASE}/${id}`, { method: 'DELETE' });
}

export async function runNow(id: string): Promise<{ status: string }> {
  const res = await authFetch(`${BASE}/${id}/run-now`, { method: 'POST' });
  return res.json();
}

export async function stopScheduledRun(id: string): Promise<{ stopped: boolean }> {
  const res = await authFetch(`${BASE}/${id}/stop`, { method: 'POST' });
  return res.json();
}

export async function listExecutions(scheduleId: string): Promise<Execution[]> {
  const res = await authFetch(`${BASE}/${scheduleId}/executions`);
  return res.json();
}
